import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Radio, CheckCircle2, AlertTriangle, ExternalLink, Copy, Info, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Radio → Templates panel.
 *
 * Shows every Gupshup template registered for the app so the CEO can see
 * which template to use for staff training drops (template messages deliver
 * outside the 24-hour session window, unlike free-form text).
 *
 * The "configured" template — set via `GUPSHUP_TEMPLATE_ID` env var — is
 * highlighted. A how-to card guides the user to create a ScaleBoard-specific
 * training template and have it Meta-approved.
 */
export default function RadioTemplatesPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [q, setQ] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/radio/templates')
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load templates'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const submitRadioTemplate = async () => {
    setSubmitting(true);
    try {
      const r = await api.post('/radio/templates/submit', {});
      toast.success(`Submitted for Meta approval · ID: ${r.data.template_id?.slice(0, 8)}…`);
      setTimeout(load, 1500); // refresh list
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed (template may already exist)');
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const filtered = (data?.items || []).filter((t) => {
    const ql = q.trim().toLowerCase();
    if (!ql) return true;
    return (
      (t.element_name || '').toLowerCase().includes(ql)
      || (t.body || '').toLowerCase().includes(ql)
      || (t.category || '').toLowerCase().includes(ql)
      || (t.id || '').toLowerCase().includes(ql)
    );
  });

  const approved = filtered.filter((t) => t.status === 'APPROVED');
  const other = filtered.filter((t) => t.status !== 'APPROVED');

  if (loading) {
    return <div className="flex items-center justify-center py-10" data-testid="radio-templates-loading">
      <div className="w-5 h-5 border-2 border-[#2A7B88] border-t-transparent rounded-full animate-spin" />
    </div>;
  }

  const suggestedBody = `🎙️ Hi {{1}}! Today's 2-min ScaleBoard training:

*{{2}}*
{{3}}

— ScaleBoard`;

  return (
    <div className="space-y-4" data-testid="radio-templates-panel">
      {/* Header + status */}
      <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#2A7B88' }}>
              <Radio size={15} style={{ color: '#FFF' }} />
            </div>
            <div>
              <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>WhatsApp Templates</h3>
              <p className="text-[11px]" style={{ color: '#6B7280' }}>
                Pre-approved messages that deliver to staff who have <b>never</b> messaged ScaleBoard (outside the 24h session window).
              </p>
            </div>
          </div>
          <a href="https://console.gupshup.io" target="_blank" rel="noreferrer"
            className="text-[11px] px-2 py-1 rounded border inline-flex items-center gap-1 shrink-0"
            style={{ borderColor: '#E5E1DC', color: '#2A7B88' }}
            data-testid="gupshup-console-link">
            <ExternalLink size={10} /> Gupshup Console
          </a>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
          <div className="rounded border px-2.5 py-2" style={{ borderColor: '#E5E1DC' }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>App Name</p>
            <p className="font-mono truncate" style={{ color: '#1B2A4A' }}>{data?.app_name || '—'}</p>
          </div>
          <div className="rounded border px-2.5 py-2" style={{ borderColor: '#E5E1DC' }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>App ID</p>
            <p className="font-mono truncate" style={{ color: '#1B2A4A' }}>{data?.app_id || '—'}</p>
          </div>
          <div className="rounded border px-2.5 py-2" style={{ borderColor: '#E5E1DC' }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Approved templates</p>
            <p className="font-semibold" style={{ color: '#1B2A4A' }}>{(data?.items || []).filter((t) => t.status === 'APPROVED').length}</p>
          </div>
          <div className="rounded border px-2.5 py-2"
            style={{ borderColor: data?.template_configured ? '#2D8B5E' : '#F59E0B', backgroundColor: data?.template_configured ? '#ECFDF5' : '#FEF3E5' }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Radio send mode</p>
            <p className="font-semibold" style={{ color: data?.template_configured ? '#2D8B5E' : '#8A5A00' }}>
              {data?.template_configured ? 'Template (works anytime)' : 'Session text only'}
            </p>
          </div>
        </div>
      </div>

      {/* How to register a ScaleBoard training template */}
      {!data?.template_configured && (
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#F59E0B' }} data-testid="radio-templates-howto">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={14} style={{ color: '#8A5A00' }} />
            <h3 className="text-sm font-bold" style={{ color: '#8A5A00' }}>No training template registered yet</h3>
          </div>
          <p className="text-[11px] mb-3" style={{ color: '#6B7280' }}>
            Currently Radio uses <b>session text messages</b> — those only deliver if the staff has messaged the business in the last 24 hours.
            Register a template below to reach staff anytime.
          </p>
          <ol className="space-y-1.5 text-[11px] list-decimal pl-5" style={{ color: '#1B2A4A' }}>
            <li>Open <a href="https://console.gupshup.io" target="_blank" rel="noreferrer" className="underline" style={{ color: '#2A7B88' }}>Gupshup Console</a> → Templates → <b>Create Template</b></li>
            <li>Template Name: <code className="px-1 rounded" style={{ backgroundColor: '#F3F0EB' }}>scaleboard_radio_drop</code> (lowercase, underscores only)</li>
            <li>Category: <b>Utility</b> (free tier for transactional training content)</li>
            <li>Language: <b>en</b></li>
            <li>
              Body (copy this exactly):
              <div className="mt-1 rounded border p-2 font-mono text-[10px] whitespace-pre-wrap relative" style={{ borderColor: '#E5E1DC', backgroundColor: '#F9FAFB' }}>
                {suggestedBody}
                <button onClick={() => copy(suggestedBody)}
                  className="absolute top-1 right-1 p-1 rounded border bg-white"
                  style={{ borderColor: '#E5E1DC', color: '#2A7B88' }}
                  data-testid="radio-templates-copy-body">
                  <Copy size={10} />
                </button>
              </div>
            </li>
            <li>Sample values for each variable (Meta requires these to approve):
              <ul className="list-disc pl-5 mt-0.5 text-[11px]" style={{ color: '#6B7280' }}>
                <li>{'{{1}}'} → <code className="px-1 rounded" style={{ backgroundColor: '#F3F0EB' }}>Ramesh</code></li>
                <li>{'{{2}}'} → <code className="px-1 rounded" style={{ backgroundColor: '#F3F0EB' }}>Our Story — The Heart of MindEscapes</code></li>
                <li>{'{{3}}'} → <code className="px-1 rounded" style={{ backgroundColor: '#F3F0EB' }}>https://lesconciergesgroup.com/r/abc123</code></li>
              </ul>
            </li>
            <li>Submit for Meta approval (usually 15 min – 24 h)</li>
            <li>Once <span className="inline-block px-1.5 rounded text-[10px] font-semibold" style={{ backgroundColor: '#ECFDF5', color: '#2D8B5E' }}>APPROVED</span>, copy the template ID and share with me (or edit <code className="px-1 rounded" style={{ backgroundColor: '#F3F0EB' }}>GUPSHUP_TEMPLATE_ID</code> in <code className="px-1 rounded" style={{ backgroundColor: '#F3F0EB' }}>backend/.env</code>)</li>
          </ol>

          {/* One-click submit — does steps 1–6 in a single API call */}
          <div className="mt-3 pt-3 border-t" style={{ borderColor: '#F3F0EB' }}>
            <p className="text-[11px] mb-2" style={{ color: '#6B7280' }}>
              <b>Or skip all of the above</b> — click below and I'll submit the exact template shown above to Meta via Gupshup's API:
            </p>
            <button onClick={submitRadioTemplate} disabled={submitting}
              className="px-4 py-2 rounded-md text-xs font-semibold text-white flex items-center gap-1.5 disabled:opacity-60"
              style={{ backgroundColor: '#2A7B88' }}
              data-testid="radio-templates-submit-auto">
              {submitting ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              {submitting ? 'Submitting…' : 'Submit scaleboard_radio_drop for Meta approval'}
            </button>
            <p className="text-[10px] mt-1.5" style={{ color: '#9CA3AF' }}>
              After approval (usually 15 min – 24 h), ScaleBoard Radio auto-switches to template mode — works for every staff member, anytime.
            </p>
          </div>
        </div>
      )}

      {/* Configured template call-out */}
      {data?.template_configured && (
        <div className="bg-white rounded-xl border p-3 flex items-start gap-2" style={{ borderColor: '#2D8B5E', backgroundColor: '#ECFDF5' }}>
          <CheckCircle2 size={16} style={{ color: '#2D8B5E' }} className="shrink-0 mt-0.5" />
          <div className="flex-1 text-[11px]" style={{ color: '#1B2A4A' }}>
            <p className="font-semibold mb-0.5">Template mode ACTIVE</p>
            <p style={{ color: '#6B7280' }}>
              Radio drops will be sent via the template <code className="px-1 rounded font-mono" style={{ backgroundColor: '#FFF' }}>{data.configured_template_id}</code> — works for every staff member regardless of whether they've messaged the business before.
            </p>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-xl border p-3" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-center justify-between mb-2 gap-2">
          <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Registered templates</h3>
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name / body / category…"
            className="text-[11px] px-2 py-1 rounded border flex-1 max-w-xs"
            style={{ borderColor: '#E5E1DC' }}
            data-testid="radio-templates-search"
          />
        </div>

        <div className="divide-y" style={{ borderColor: '#F3F0EB' }}>
          {[...approved, ...other].map((t) => {
            const isConfigured = data?.configured_template_id === t.id;
            return (
              <div key={t.id} className="py-2.5 flex items-start justify-between gap-2"
                style={{ backgroundColor: isConfigured ? '#ECFDF5' : 'transparent' }}
                data-testid={`radio-template-${t.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>{t.element_name}</p>
                    <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold"
                      style={{
                        backgroundColor: t.status === 'APPROVED' ? '#ECFDF5' : (t.status === 'FAILED' ? '#FEF2F2' : '#FEF3E5'),
                        color: t.status === 'APPROVED' ? '#2D8B5E' : (t.status === 'FAILED' ? '#D4726A' : '#8A5A00'),
                      }}>
                      {t.status}
                    </span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold"
                      style={{ backgroundColor: '#F3F0EB', color: '#6B7280' }}>
                      {t.category}
                    </span>
                    <span className="text-[9px]" style={{ color: '#9CA3AF' }}>{t.language}</span>
                    {isConfigured && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded font-bold"
                        style={{ backgroundColor: '#2D8B5E', color: '#FFF' }}>
                        ✓ RADIO DEFAULT
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] font-mono mt-0.5" style={{ color: '#9CA3AF' }}>{t.id}</p>
                  <p className="text-[11px] mt-1 whitespace-pre-wrap line-clamp-3" style={{ color: '#6B7280' }}>{t.body}</p>
                </div>
                <button onClick={() => copy(t.id)}
                  className="text-[10px] px-2 py-1 rounded border flex items-center gap-1 shrink-0"
                  style={{ borderColor: '#E5E1DC', color: '#2A7B88' }}
                  data-testid={`radio-template-copy-${t.id}`}>
                  <Copy size={9} /> Copy ID
                </button>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <p className="text-center text-[11px] py-4" style={{ color: '#9CA3AF' }}>No templates match "{q}"</p>
          )}
        </div>
      </div>

      {/* Info footnote */}
      <div className="flex items-start gap-2 text-[10px]" style={{ color: '#9CA3AF' }}>
        <Info size={11} className="shrink-0 mt-0.5" />
        <p>Templates listed here are fetched live from Gupshup on every load. After Meta approves a new template, it will appear automatically — no ScaleBoard redeploy needed.</p>
      </div>
    </div>
  );
}
